import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AttachmentService } from '../../../../../libs/shared/src/lib/services/attachment.service';
import { Attachment, BodyAttachmentResponse } from '../../../../../libs/shared/src/lib/models/attachment.model';
import { UserFromBE } from '../../../../../libs/shared/src/lib/models/dashboard/user-from-be';
import { AuthService } from './auth.service';

export interface AvatarUploadResult {
  photoID: number;
  url?: string;
  thumbnail?: string;
}

/**
 * Centralizes the candidate avatar (profile photo) flow for the jobs app.
 *
 * Backend contract is isolated here behind a thin wrapper.
 *
 * Contract (see sameta-sk-be `/upload-candidate-profile-picture`):
 *  - Upload generates a thumbnail and sets `users.photo_id`. The response is the
 *    created attachment (`id` = photoID, `url`, `thumbnail`).
 *  - Full-res reads go through `get-attachments-id` (HR attachment endpoint).
 *  - The candidate's `UserFromBE` (login / auth-user) carries `photoThumbnail`
 *    (base64 JPEG) and `photoID` for read-back.
 */
@Injectable({
  providedIn: 'root',
})
export class AvatarService {
  constructor(
    private readonly attachmentService: AttachmentService,
    private readonly authService: AuthService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  /**
   * Resolve the avatar source for the current user without an extra request when
   * a thumbnail is available.
   * @returns a `data:` URL string (thumbnail) synchronously, otherwise null.
   */
  getThumbnailDataUrl(user: UserFromBE | null): string | null {
    if (user?.photoThumbnail) {
      // photoThumbnail is a raw base64 JPEG string (matches sameta-fe HR app).
      return `data:image/jpeg;base64,${user.photoThumbnail}`;
    }
    return null;
  }

  /**
   * Download the avatar blob by id and return a sanitized object URL.
   */
  getAvatarUrl(photoID: number | string): Observable<SafeUrl | null> {
    if (!photoID || photoID === 0) {
      return of(null);
    }
    return this.attachmentService.downloadProfilePicture(String(photoID)).pipe(
      map((response: any) => {
        const dataType = response?.body?.type;
        const objectUrl = globalThis.URL.createObjectURL(new Blob([response.body], { type: dataType }));
        return this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      }),
    );
  }

  /**
   * Upload a cropped avatar blob and persist the resulting photoID on the
   * local user state (so the profile page + header update immediately).
   */
  uploadAvatar(blob: Blob, fileType: string, fileName: string): Observable<AvatarUploadResult> {
    return this.attachmentService.sendCandidateProfilePicture(blob, fileType, fileName).pipe(
      map((response: BodyAttachmentResponse) => {
        const attachment = (Array.isArray(response.body) ? response.body[0] : response.body) as Attachment;
        const result: AvatarUploadResult = { photoID: attachment.id, url: attachment.url, thumbnail: attachment.thumbnail };
        // Sync cached user so the header (which reads photoThumbnail) updates at once.
        this.syncLocalUser({ photoID: attachment.id, photoThumbnail: attachment.thumbnail });
        return result;
      }),
    );
  }

  /**
   * Remove the avatar: clears users.photo_id server-side, then syncs local state.
   */
  clearAvatar(): Observable<void> {
    return this.attachmentService.deleteCandidateProfilePicture().pipe(
      map(() => {
        this.syncLocalUser({ photoID: 0, photoThumbnail: undefined });
      }),
    );
  }

  /**
   * Patch the cached user (BehaviorSubject + localStorage) so the avatar shows
   * up everywhere without a full refresh. Mirrors sameta-fe settings flow.
   */
  private syncLocalUser(patch: Partial<UserFromBE>): void {
    const current = this.authService.getCurrentUser();
    if (!current) {
      return;
    }
    const updated: UserFromBE = { ...current, ...patch };
    localStorage.setItem('user', JSON.stringify(updated));
    this.authService.currentUserValue = updated;
  }
}
